import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  IconButton,
  Paper,
  Divider,
  Box,
  CircularProgress,
  Alert,
  Rating,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { Doctor } from '../../types';
import { categoryService, Category } from '../../services/categoryService';

interface DoctorFormProps {
  error: string;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  doctor: Doctor | null;
  submitting?: boolean;
  loadingFormData?: boolean;
  formData: {
    fullName: string;
    email: string;
    phoneNumber: string;
    specialization: string;
    isActive: boolean;
    password: string;
    dateOfBirth?: string;
    gender?: string;
    address?: string;
    departmentId?: number;
    experience?: string;
    qualifications?: string;
    licenseNumber?: string;
    aboutYourself?: string;
    profileImage?: File | null;
    services: {
      chat: { price: number; isEnabled: boolean; duration: number; description: string };
      audioCall: { price: number; isEnabled: boolean; duration: number; description: string };
      videoCall: { price: number; isEnabled: boolean; duration: number; description: string };
    };
    degrees: Array<{ degree: string; institution: string; year: string }>;
    availability: Array<{
      day: string;
      isAvailable: boolean;
      timeRanges: Array<{
        startTime: string;
        endTime: string;
        slotDuration: number;
      }>;
    }>;
    ratings: Array<{
      id?: number;
      patientId?: number | null;
      patientName?: string;
      patientProfileImage?: string;
      rating: number;
      message: string;
    }>;
  };
  onInputChange: (field: string, value: any) => void;
}

const DoctorForm: React.FC<DoctorFormProps> = ({
  error,
  open,
  onClose,
  onSave,
  doctor,
  formData,
  onInputChange,
  submitting = false,
  loadingFormData = false,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    if (open) {
      loadCategories();
    }
  }, [open]);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categoryService.getCategories({ limit: 100 });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onInputChange('profileImage', file);
  };

  const handleServiceChange = (serviceType: 'chat' | 'audioCall' | 'videoCall', field: string, value: any) => {
    const updatedServices = {
      ...formData.services,
      [serviceType]: {
        ...formData.services[serviceType],
        [field]: value,
      },
    };
    onInputChange('services', updatedServices);
  };

  const handleDegreeChange = (index: number, field: string, value: string) => {
    const updatedDegrees = [...formData.degrees];
    updatedDegrees[index] = { ...updatedDegrees[index], [field]: value };
    onInputChange('degrees', updatedDegrees);
  };

  const addDegree = () => {
    onInputChange('degrees', [...formData.degrees, { degree: '', institution: '', year: '' }]);
  };

  const removeDegree = (index: number) => {
    const updatedDegrees = formData.degrees.filter((_, i) => i !== index);
    onInputChange('degrees', updatedDegrees);
  };

  const handleAvailabilityChange = (dayIndex: number, field: string, value: any) => {
    const updatedAvailability = [...formData.availability];
    updatedAvailability[dayIndex] = { ...updatedAvailability[dayIndex], [field]: value };
    onInputChange('availability', updatedAvailability);
  };

  const handleTimeRangeChange = (dayIndex: number, rangeIndex: number, field: string, value: any) => {
    const updatedAvailability = [...formData.availability];
    const updatedTimeRanges = [...updatedAvailability[dayIndex].timeRanges];
    updatedTimeRanges[rangeIndex] = { ...updatedTimeRanges[rangeIndex], [field]: value };
    updatedAvailability[dayIndex] = { ...updatedAvailability[dayIndex], timeRanges: updatedTimeRanges };
    onInputChange('availability', updatedAvailability);
  };

  const addTimeRange = (dayIndex: number) => {
    const updatedAvailability = [...formData.availability];
    updatedAvailability[dayIndex].timeRanges.push({
      startTime: '09:00 AM',
      endTime: '05:00 PM',
      slotDuration: 30,
    });
    onInputChange('availability', updatedAvailability);
  };

  const removeTimeRange = (dayIndex: number, rangeIndex: number) => {
    const updatedAvailability = [...formData.availability];
    updatedAvailability[dayIndex].timeRanges = updatedAvailability[dayIndex].timeRanges.filter(
      (_, i) => i !== rangeIndex
    );
    onInputChange('availability', updatedAvailability);
  };

  const handleRatingChange = (index: number, field: string, value: any) => {
    const updatedRatings = [...formData.ratings];
    updatedRatings[index] = { ...updatedRatings[index], [field]: value };
    onInputChange('ratings', updatedRatings);
  };

  const addRating = () => {
    onInputChange('ratings', [...formData.ratings, { patientName: '', rating: 0, message: '' }]);
  };

  const removeRating = (index: number) => {
    const updatedRatings = formData.ratings.filter((_, i) => i !== index);
    onInputChange('ratings', updatedRatings);
  };

  // Helper to convert 24-hour time to 12-hour format
  const convertTo12Hour = (time24: string): string => {
    if (!time24) return '09:00 AM';
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Helper to convert 12-hour time to 24-hour format for HTML time input
  const convertTo24Hour = (time12: string): string => {
    if (!time12) return '09:00';
    const [time, period] = time12.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let hours24 = hours;
    if (period === 'PM' && hours !== 12) {
      hours24 = hours + 12;
    } else if (period === 'AM' && hours === 12) {
      hours24 = 0;
    }
    return `${hours24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Helper to convert 24-hour HTML time input to 12-hour format
  const handleTimeInputChange = (dayIndex: number, rangeIndex: number, field: 'startTime' | 'endTime', time24: string) => {
    const time12 = convertTo12Hour(time24);
    handleTimeRangeChange(dayIndex, rangeIndex, field, time12);
  };

  const dayNames: { [key: string]: string } = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
  };

  const handleDialogClose = (event: {}, reason: string) => {
    // Only allow closing via the close button or cancel button, not by clicking outside
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleDialogClose} 
      maxWidth="lg" 
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        {doctor ? 'Edit Doctor' : 'Add New Doctor'}
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loadingFormData ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ mt: 0 }}>
          {/* Basic Information */}
          <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
              Basic Information
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Name"
              value={formData.fullName}
              onChange={(e) => onInputChange('fullName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => onInputChange('email', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => onInputChange('phoneNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date of Birth"
              type="date"
              value={formData.dateOfBirth || ''}
              onChange={(e) => onInputChange('dateOfBirth', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                label="Gender"
                value={formData.gender || ''}
                onChange={(e) => onInputChange('gender', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Address"
              value={formData.address || ''}
              onChange={(e) => onInputChange('address', e.target.value)}
              multiline
              rows={2}
            />
          </Grid>

          {/* Professional Information */}
          <Grid item xs={12} sx={{ mt: 5, mb: 1 }}>
            <Divider sx={{ mb: 3, mt: 1 }} />
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
              Professional Information
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                label="Department"
                value={formData.departmentId || ''}
                onChange={(e) => onInputChange('departmentId', e.target.value ? Number(e.target.value) : null)}
                disabled={loadingCategories}
              >
                <MenuItem value="">None</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Specialization"
              placeholder="e.g. Ayurveda, Panchakarma"
              value={formData.specialization || ''}
              onChange={(e) => onInputChange('specialization', e.target.value)}
              helperText="Separate multiple specialties with commas"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Experience"
              value={formData.experience || ''}
              onChange={(e) => onInputChange('experience', e.target.value)}
              multiline
              rows={2}
              placeholder="e.g. 10 years of experience in Ayurveda"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Qualifications"
              value={formData.qualifications || ''}
              onChange={(e) => onInputChange('qualifications', e.target.value)}
              multiline
              rows={2}
              placeholder="e.g. BAMS, MD in Ayurveda"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="License Number"
              value={formData.licenseNumber || ''}
              onChange={(e) => onInputChange('licenseNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="About Yourself"
              value={formData.aboutYourself || ''}
              onChange={(e) => onInputChange('aboutYourself', e.target.value)}
              multiline
              rows={3}
              placeholder="Tell us about yourself..."
            />
          </Grid>

          {/* Profile Image */}
          <Grid item xs={12} sx={{ mt: 5, mb: 1 }}>
            <Divider sx={{ mb: 3, mt: 1 }} />
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
              Profile Image
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="profile-image-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="profile-image-upload">
              <Button variant="outlined" component="span" fullWidth>
                {formData.profileImage ? 'Change Image' : 'Upload Profile Image'}
              </Button>
            </label>
            {formData.profileImage && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Selected: {formData.profileImage.name}
              </Typography>
            )}
          </Grid>

          {/* Account Settings */}
          <Grid item xs={12} sx={{ mt: 5, mb: 1 }}>
            <Divider sx={{ mb: 3, mt: 1 }} />
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
              Account Settings
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => onInputChange('isActive', e.target.checked)}
                  color="primary"
                />
              }
              label="Active"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => onInputChange('password', e.target.value)}
              placeholder={doctor ? 'Leave blank to keep current password' : 'Optional'}
              helperText={doctor ? 'Leave blank to keep the current password' : 'Optional - can be set later'}
            />
          </Grid>

          {/* Services Section */}
          <Grid item xs={12} sx={{ mt: 5, mb: 1 }}>
            <Divider sx={{ mb: 3, mt: 1 }} />
            <Typography variant="h6" sx={{ mb: 4, fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
              Services & Pricing
            </Typography>
          </Grid>

          {/* Chat Service */}
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Paper elevation={1} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2.5, fontWeight: 600, color: 'text.primary' }}>Chat Service</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.services.chat.isEnabled}
                        onChange={(e) => handleServiceChange('chat', 'isEnabled', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Chat"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Price (₹)"
                    type="number"
                    value={formData.services.chat.price}
                    onChange={(e) => handleServiceChange('chat', 'price', parseFloat(e.target.value) || 0)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Duration (minutes)"
                    type="number"
                    value={formData.services.chat.duration}
                    onChange={(e) => handleServiceChange('chat', 'duration', parseInt(e.target.value) || 30)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.services.chat.description}
                    onChange={(e) => handleServiceChange('chat', 'description', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Audio Call Service */}
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Paper elevation={1} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2.5, fontWeight: 600, color: 'text.primary' }}>Audio Call Service</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.services.audioCall.isEnabled}
                        onChange={(e) => handleServiceChange('audioCall', 'isEnabled', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Audio Call"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Price (₹)"
                    type="number"
                    value={formData.services.audioCall.price}
                    onChange={(e) => handleServiceChange('audioCall', 'price', parseFloat(e.target.value) || 0)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Duration (minutes)"
                    type="number"
                    value={formData.services.audioCall.duration}
                    onChange={(e) => handleServiceChange('audioCall', 'duration', parseInt(e.target.value) || 30)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.services.audioCall.description}
                    onChange={(e) => handleServiceChange('audioCall', 'description', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Video Call Service */}
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Paper elevation={1} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2.5, fontWeight: 600, color: 'text.primary' }}>Video Call Service</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.services.videoCall.isEnabled}
                        onChange={(e) => handleServiceChange('videoCall', 'isEnabled', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Video Call"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Price (₹)"
                    type="number"
                    value={formData.services.videoCall.price}
                    onChange={(e) => handleServiceChange('videoCall', 'price', parseFloat(e.target.value) || 0)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Duration (minutes)"
                    type="number"
                    value={formData.services.videoCall.duration}
                    onChange={(e) => handleServiceChange('videoCall', 'duration', parseInt(e.target.value) || 30)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.services.videoCall.description}
                    onChange={(e) => handleServiceChange('videoCall', 'description', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Degrees Section */}
          <Grid item xs={12} sx={{ mt: 5, mb: 1 }}>
            <Divider sx={{ mb: 3, mt: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
                Degrees
              </Typography>
              <Button startIcon={<Add />} onClick={addDegree} size="small" variant="outlined">
                Add Degree
              </Button>
            </Box>
          </Grid>

          {formData.degrees.map((degree, index) => (
            <Grid item xs={12} key={index} sx={{ mb: 3 }}>
              <Paper elevation={1} sx={{ p: 3, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Degree"
                      value={degree.degree}
                      onChange={(e) => handleDegreeChange(index, 'degree', e.target.value)}
                      placeholder="e.g. MBBS, MD"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Institution"
                      value={degree.institution}
                      onChange={(e) => handleDegreeChange(index, 'institution', e.target.value)}
                      placeholder="University/College name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Year"
                      value={degree.year}
                      onChange={(e) => handleDegreeChange(index, 'year', e.target.value)}
                      placeholder="e.g. 2010"
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton onClick={() => removeDegree(index)} color="error">
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}

          {/* Availability Section */}
          <Grid item xs={12} sx={{ mt: 5, mb: 1 }}>
            <Divider sx={{ mb: 3, mt: 1 }} />
            <Typography variant="h6" sx={{ mb: 4, fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
              Weekly Availability
            </Typography>
          </Grid>

          {formData.availability.map((day, dayIndex) => (
            <Grid item xs={12} key={day.day} sx={{ mb: 3 }}>
              <Paper elevation={1} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={day.isAvailable}
                            onChange={(e) => {
                              handleAvailabilityChange(dayIndex, 'isAvailable', e.target.checked);
                              if (!e.target.checked) {
                                handleAvailabilityChange(dayIndex, 'timeRanges', []);
                              } else if (day.timeRanges.length === 0) {
                                handleAvailabilityChange(dayIndex, 'timeRanges', [{
                                  startTime: '09:00 AM',
                                  endTime: '05:00 PM',
                                  slotDuration: 30,
                                }]);
                              }
                            }}
                            color="primary"
                          />
                        }
                        label={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{dayNames[day.day] || day.day}</Typography>}
                      />
                      {day.isAvailable && (
                        <Button
                          startIcon={<Add />}
                          onClick={() => addTimeRange(dayIndex)}
                          size="small"
                          variant="outlined"
                        >
                          Add Time Range
                        </Button>
                      )}
                    </Box>
                  </Grid>
                  {day.isAvailable && day.timeRanges.length > 0 && (
                    <>
                      {day.timeRanges.map((timeRange, rangeIndex) => (
                        <Grid item xs={12} key={rangeIndex}>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  label="Start Time"
                                  type="time"
                                  value={convertTo24Hour(timeRange.startTime)}
                                  onChange={(e) => handleTimeInputChange(dayIndex, rangeIndex, 'startTime', e.target.value)}
                                  InputLabelProps={{ shrink: true }}
                                />
                                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                  {timeRange.startTime}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  label="End Time"
                                  type="time"
                                  value={convertTo24Hour(timeRange.endTime)}
                                  onChange={(e) => handleTimeInputChange(dayIndex, rangeIndex, 'endTime', e.target.value)}
                                  InputLabelProps={{ shrink: true }}
                                />
                                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                  {timeRange.endTime}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField
                                  fullWidth
                                  label="Slot Duration (min)"
              type="number"
                                  value={timeRange.slotDuration}
                                  onChange={(e) => handleTimeRangeChange(dayIndex, rangeIndex, 'slotDuration', parseInt(e.target.value) || 30)}
                                  inputProps={{ min: 15, max: 120 }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <IconButton
                                  onClick={() => removeTimeRange(dayIndex, rangeIndex)}
                                  color="error"
                                  disabled={day.timeRanges.length === 1}
                                >
                                  <Delete />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      ))}
                    </>
                  )}
                  {day.isAvailable && day.timeRanges.length === 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No time ranges added. Click "Add Time Range" to add availability.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          ))}

          {/* Ratings & Reviews Section */}
          <Grid item xs={12} sx={{ mt: 5, mb: 1 }}>
            <Divider sx={{ mb: 3, mt: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
                Ratings & Reviews
              </Typography>
              <Button startIcon={<Add />} onClick={addRating} size="small" variant="outlined">
                Add Rating
              </Button>
            </Box>
          </Grid>

          {formData.ratings.map((rating, index) => (
            <Grid item xs={12} key={index} sx={{ mb: 3 }}>
              <Paper elevation={1} sx={{ p: 3, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Patient Name"
                      value={rating.patientName || ''}
                      onChange={(e) => handleRatingChange(index, 'patientName', e.target.value)}
                      placeholder="Enter patient name"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Rating
                      </Typography>
                      <Rating
                        value={rating.rating}
                        onChange={(event: React.SyntheticEvent, newValue: number | null) => handleRatingChange(index, 'rating', newValue || 0)}
                        size="large"
                        sx={{ fontSize: '2rem' }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton onClick={() => removeRating(index)} color="error">
                        <Delete />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Review Message"
                      value={rating.message}
                      onChange={(e) => handleRatingChange(index, 'message', e.target.value)}
                      multiline
                      rows={3}
                      placeholder="Write a review message..."
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={onSave} 
          variant="contained" 
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {submitting ? (doctor ? 'Updating...' : 'Creating...') : (doctor ? 'Update' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DoctorForm;
